import { supabase, supabaseAnon } from '../lib/supabase'
import {
  handleQuery,
  accessDeniedResponse,
  resolveRoleContext,
  applyFilters,
  formatSupabaseError
} from './utils'

const FORMS_TABLE = 'marketing_forms'
const BRAND_TEMPLATES_TABLE = 'brand_templates'
const QUESTIONS_TABLE = 'marketing_form_questions'
const OPTIONS_TABLE = 'marketing_form_options'
const RESPONSES_TABLE = 'marketing_form_responses'
const TRIGGERS_TABLE = 'marketing_form_workflow_triggers'

const FORM_NESTED_QUESTIONS_SELECT = `
  marketing_form_questions (
    *,
    marketing_form_options (*)
  )
`

const FORM_WITH_BRANDING_SELECT = `
  *,
  brand_templates (*),
  ${FORM_NESTED_QUESTIONS_SELECT}
`

const FORM_WITH_QUESTIONS_SELECT = `
  *,
  ${FORM_NESTED_QUESTIONS_SELECT}
`

const RESPONSE_WITH_FORM_SELECT = `
  *,
  marketing_forms (
    id,
    title,
    slug,
    type,
    language
  )
`

const RESPONSE_DETAIL_SELECT = `
  *,
  marketing_form_answers (
    *,
    marketing_form_questions (
      id,
      question_text_es,
      question_text_en,
      type,
      order,
      required
    ),
    marketing_form_options (
      id,
      option_text_es,
      option_text_en,
      is_correct
    )
  )
`

/**
 * @param {Array<{ order?: number }> | null | undefined} items
 */
const sortByOrder = (items) => {
  if (!Array.isArray(items)) return items
  return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

/**
 * @param {object | null} form
 */
const normalizeNestedForm = (form) => {
  if (!form) return form
  const questions = sortByOrder(form.marketing_form_questions)?.map((question) => ({
    ...question,
    marketing_form_options: sortByOrder(question.marketing_form_options)
  }))
  return { ...form, marketing_form_questions: questions }
}

/**
 * @param {Record<string, any>} data
 */
const normalizeFormPayload = (data = {}) => ({
  title: data.title,
  description: data.description ?? null,
  type: data.type,
  language: data.language ?? 'es',
  status: data.status ?? 'draft',
  slug: data.slug != null ? String(data.slug).trim().toLowerCase() : data.slug,
  custom_domain: data.custom_domain ?? data.customDomain ?? null,
  event_id: data.event_id ?? data.eventId ?? null,
  pass_score: data.pass_score ?? data.passScore ?? null,
  show_results_to_respondent:
    data.show_results_to_respondent ?? data.showResultsToRespondent ?? false,
  brand_template_id: data.brand_template_id ?? data.brandTemplateId ?? null,
  custom_branding: data.custom_branding ?? data.customBranding ?? null
})

/**
 * @param {Record<string, any>} data
 */
const normalizeBrandTemplatePayload = (data = {}) => ({
  name: data.name,
  logo_url: data.logo_url ?? data.logoUrl ?? null,
  primary_color: data.primary_color ?? data.primaryColor ?? null,
  secondary_color: data.secondary_color ?? data.secondaryColor ?? null,
  background_color: data.background_color ?? data.backgroundColor ?? null,
  text_color: data.text_color ?? data.textColor ?? null,
  font_family: data.font_family ?? data.fontFamily ?? null,
  custom_css: data.custom_css ?? data.customCss ?? null,
  is_default: data.is_default ?? data.isDefault ?? false
})

/**
 * @param {Record<string, any>} data
 */
const normalizeQuestionPayload = (data = {}) => ({
  form_id: data.form_id ?? data.formId,
  question_text_es: data.question_text_es ?? data.questionTextEs,
  question_text_en: data.question_text_en ?? data.questionTextEn ?? null,
  type: data.type,
  order: data.order ?? 0,
  required: data.required ?? true,
  correct_answer: data.correct_answer ?? data.correctAnswer ?? null,
  scale_min: data.scale_min ?? data.scaleMin ?? null,
  scale_max: data.scale_max ?? data.scaleMax ?? null,
  scale_min_label_es: data.scale_min_label_es ?? data.scaleMinLabelEs ?? null,
  scale_min_label_en: data.scale_min_label_en ?? data.scaleMinLabelEn ?? null,
  scale_max_label_es: data.scale_max_label_es ?? data.scaleMaxLabelEs ?? null,
  scale_max_label_en: data.scale_max_label_en ?? data.scaleMaxLabelEn ?? null
})

/**
 * @param {Record<string, any>} data
 */
const normalizeOptionPayload = (data = {}) => ({
  question_id: data.question_id ?? data.questionId,
  option_text_es: data.option_text_es ?? data.optionTextEs,
  option_text_en: data.option_text_en ?? data.optionTextEn ?? null,
  order: data.order ?? 0,
  is_correct: data.is_correct ?? data.isCorrect ?? false
})

/**
 * @param {Record<string, any>} data
 */
const normalizeTriggerPayload = (data = {}) => ({
  form_id: data.form_id ?? data.formId,
  condition_key: data.condition_key ?? data.conditionKey ?? null,
  condition_value: data.condition_value ?? data.conditionValue ?? null,
  workflow_trigger_event: data.workflow_trigger_event ?? data.workflowTriggerEvent
})

/**
 * @param {{ role?: string, userId?: string }} [options]
 */
const requireAdmin = async (options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { error: roleContext.error }
  if (roleContext.role !== 'admin') {
    return {
      error: accessDeniedResponse('Marketing forms access is limited to administrators.').error
    }
  }
  return { role: roleContext.role, userId: roleContext.userId, error: null }
}

/**
 * @param {{ role?: string, userId?: string }} [options]
 */
const requireAgentOrAdmin = async (options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { error: roleContext.error }
  if (roleContext.role !== 'agent' && roleContext.role !== 'admin') {
    return {
      error: accessDeniedResponse('Only agents and administrators can access form responses.').error
    }
  }
  return { role: roleContext.role, userId: roleContext.userId, error: null }
}

// --- Admin: forms ---

/**
 * List all marketing forms with optional filters (admin only; RLS enforced).
 * @param {{ type?: string, status?: string, eventId?: string, role?: string, userId?: string }} [filters]
 */
export const listForms = async ({ type, status, eventId, role, userId } = {}) => {
  const ctx = await requireAdmin({ role, userId })
  if (ctx.error) return { data: null, error: ctx.error }

  const tableFilters = []
  if (type) tableFilters.push({ column: 'type', operator: 'eq', value: type })
  if (status) tableFilters.push({ column: 'status', operator: 'eq', value: status })
  if (eventId) tableFilters.push({ column: 'event_id', operator: 'eq', value: eventId })

  let query = supabase
    .from(FORMS_TABLE)
    .select('*')
    .order('updated_at', { ascending: false })

  query = applyFilters(query, tableFilters)

  return handleQuery(query, { fallbackMessage: 'Unable to load marketing forms.' })
}

/**
 * Get a form with nested questions and options (admin only).
 * @param {string} formId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const getFormById = async (formId, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const result = await handleQuery(
    supabase
      .from(FORMS_TABLE)
      .select(FORM_WITH_BRANDING_SELECT)
      .eq('id', formId)
      .maybeSingle(),
    { fallbackMessage: 'Unable to load the marketing form.', allowEmpty: false }
  )

  if (result.error) return result
  return { data: normalizeNestedForm(result.data), error: null }
}

/**
 * Create a marketing form (admin only).
 * @param {Record<string, any>} data
 * @param {{ role?: string, userId?: string }} [options]
 */
export const createForm = async (data, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const { data: { session } } = await supabase.auth.getSession()
  const createdBy = ctx.userId ?? session?.user?.id ?? null
  if (!createdBy) {
    return accessDeniedResponse('User context is required to create a form.')
  }

  const payload = {
    ...normalizeFormPayload(data),
    created_by: createdBy
  }

  if (!payload.title || !payload.type || !payload.slug) {
    return {
      data: null,
      error: { message: 'Title, type, and slug are required.', code: 'invalid_input' }
    }
  }

  return handleQuery(
    supabase.from(FORMS_TABLE).insert(payload).select('*').single(),
    { fallbackMessage: 'Unable to create the marketing form.', allowEmpty: false }
  )
}

/**
 * Update a marketing form (admin only).
 * @param {string} formId
 * @param {Record<string, any>} data
 * @param {{ role?: string, userId?: string }} [options]
 */
export const updateForm = async (formId, data, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const payload = {}
  const fieldMap = {
    title: data.title,
    description: data.description,
    type: data.type,
    language: data.language,
    status: data.status,
    slug: data.slug,
    custom_domain: data.custom_domain ?? data.customDomain,
    event_id: data.event_id ?? data.eventId,
    pass_score: data.pass_score ?? data.passScore,
    show_results_to_respondent: data.show_results_to_respondent ?? data.showResultsToRespondent,
    brand_template_id: data.brand_template_id ?? data.brandTemplateId,
    custom_branding: data.custom_branding ?? data.customBranding
  }
  Object.entries(fieldMap).forEach(([key, value]) => {
    if (value !== undefined) payload[key] = value
  })
  if (payload.slug != null) {
    payload.slug = String(payload.slug).trim().toLowerCase()
  }

  return handleQuery(
    supabase.from(FORMS_TABLE).update(payload).eq('id', formId).select('*').single(),
    { fallbackMessage: 'Unable to update the marketing form.', allowEmpty: false }
  )
}

/**
 * Delete a marketing form and cascaded children (admin only).
 * @param {string} formId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const deleteForm = async (formId, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase.from(FORMS_TABLE).delete().eq('id', formId).select('id').single(),
    { fallbackMessage: 'Unable to delete the marketing form.', allowEmpty: false }
  )
}

// --- Admin: questions ---

/**
 * List questions for a form ordered by `order` (admin only).
 * @param {string} formId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listQuestions = async (formId, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const result = await handleQuery(
    supabase
      .from(QUESTIONS_TABLE)
      .select('*')
      .eq('form_id', formId)
      .order('order', { ascending: true }),
    { fallbackMessage: 'Unable to load form questions.' }
  )

  if (result.error) return result
  return { data: sortByOrder(result.data), error: null }
}

/**
 * @param {Record<string, any>} data
 * @param {{ role?: string, userId?: string }} [options]
 */
export const createQuestion = async (data, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const payload = normalizeQuestionPayload(data)
  if (!payload.form_id || !payload.question_text_es || !payload.type) {
    return {
      data: null,
      error: { message: 'form_id, question_text_es, and type are required.', code: 'invalid_input' }
    }
  }

  return handleQuery(
    supabase.from(QUESTIONS_TABLE).insert(payload).select('*').single(),
    { fallbackMessage: 'Unable to create the question.', allowEmpty: false }
  )
}

/**
 * @param {string} questionId
 * @param {Record<string, any>} data
 * @param {{ role?: string, userId?: string }} [options]
 */
export const updateQuestion = async (questionId, data, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const payload = { ...data }
  if (payload.formId !== undefined) payload.form_id = payload.formId
  if (payload.questionTextEs !== undefined) payload.question_text_es = payload.questionTextEs
  if (payload.questionTextEn !== undefined) payload.question_text_en = payload.questionTextEn
  if (payload.correctAnswer !== undefined) payload.correct_answer = payload.correctAnswer
  if (payload.scaleMin !== undefined) payload.scale_min = payload.scaleMin
  if (payload.scaleMax !== undefined) payload.scale_max = payload.scaleMax
  if (payload.scaleMinLabelEs !== undefined) payload.scale_min_label_es = payload.scaleMinLabelEs
  if (payload.scaleMinLabelEn !== undefined) payload.scale_min_label_en = payload.scaleMinLabelEn
  if (payload.scaleMaxLabelEs !== undefined) payload.scale_max_label_es = payload.scaleMaxLabelEs
  if (payload.scaleMaxLabelEn !== undefined) payload.scale_max_label_en = payload.scaleMaxLabelEn
  delete payload.formId
  delete payload.questionTextEs
  delete payload.questionTextEn
  delete payload.correctAnswer
  delete payload.scaleMin
  delete payload.scaleMax
  delete payload.scaleMinLabelEs
  delete payload.scaleMinLabelEn
  delete payload.scaleMaxLabelEs
  delete payload.scaleMaxLabelEn

  return handleQuery(
    supabase.from(QUESTIONS_TABLE).update(payload).eq('id', questionId).select('*').single(),
    { fallbackMessage: 'Unable to update the question.', allowEmpty: false }
  )
}

/**
 * @param {string} questionId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const deleteQuestion = async (questionId, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase.from(QUESTIONS_TABLE).delete().eq('id', questionId).select('id').single(),
    { fallbackMessage: 'Unable to delete the question.', allowEmpty: false }
  )
}

// --- Admin: options ---

/**
 * @param {Record<string, any>} data
 * @param {{ role?: string, userId?: string }} [options]
 */
export const createOption = async (data, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const payload = normalizeOptionPayload(data)
  if (!payload.question_id || !payload.option_text_es) {
    return {
      data: null,
      error: { message: 'question_id and option_text_es are required.', code: 'invalid_input' }
    }
  }

  return handleQuery(
    supabase.from(OPTIONS_TABLE).insert(payload).select('*').single(),
    { fallbackMessage: 'Unable to create the option.', allowEmpty: false }
  )
}

/**
 * @param {string} optionId
 * @param {Record<string, any>} data
 * @param {{ role?: string, userId?: string }} [options]
 */
export const updateOption = async (optionId, data, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const payload = { ...data }
  if (payload.questionId !== undefined) payload.question_id = payload.questionId
  if (payload.optionTextEs !== undefined) payload.option_text_es = payload.optionTextEs
  if (payload.optionTextEn !== undefined) payload.option_text_en = payload.optionTextEn
  if (payload.isCorrect !== undefined) payload.is_correct = payload.isCorrect
  delete payload.questionId
  delete payload.optionTextEs
  delete payload.optionTextEn
  delete payload.isCorrect

  return handleQuery(
    supabase.from(OPTIONS_TABLE).update(payload).eq('id', optionId).select('*').single(),
    { fallbackMessage: 'Unable to update the option.', allowEmpty: false }
  )
}

/**
 * @param {string} optionId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const deleteOption = async (optionId, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase.from(OPTIONS_TABLE).delete().eq('id', optionId).select('id').single(),
    { fallbackMessage: 'Unable to delete the option.', allowEmpty: false }
  )
}

// --- Admin: responses ---

/**
 * List form responses with respondent fields (admin only).
 * @param {string} formId
 * @param {{ leadId?: string, eventId?: string, role?: string, userId?: string }} [filters]
 */
export const listResponses = async (formId, { leadId, eventId, role, userId } = {}) => {
  const ctx = await requireAdmin({ role, userId })
  if (ctx.error) return { data: null, error: ctx.error }

  let query = supabase
    .from(RESPONSES_TABLE)
    .select('*')
    .eq('form_id', formId)
    .order('submitted_at', { ascending: false })

  if (leadId) query = query.eq('lead_id', leadId)
  if (eventId) query = query.eq('event_id', eventId)

  return handleQuery(query, { fallbackMessage: 'Unable to load form responses.' })
}

/**
 * Get a response with answers and related question/option text (admin or agent; RLS enforced).
 * @param {string} responseId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const getResponseById = async (responseId, options = {}) => {
  const ctx = await requireAgentOrAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase
      .from(RESPONSES_TABLE)
      .select(RESPONSE_DETAIL_SELECT)
      .eq('id', responseId)
      .maybeSingle(),
    { fallbackMessage: 'Unable to load the form response.', allowEmpty: false }
  )
}

// --- Admin: workflow triggers ---

/**
 * @param {string} formId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listWorkflowTriggers = async (formId, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase
      .from(TRIGGERS_TABLE)
      .select('*')
      .eq('form_id', formId)
      .order('created_at', { ascending: true }),
    { fallbackMessage: 'Unable to load workflow triggers.' }
  )
}

/**
 * @param {Record<string, any>} data
 * @param {{ role?: string, userId?: string }} [options]
 */
export const createWorkflowTrigger = async (data, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const payload = normalizeTriggerPayload(data)
  if (!payload.form_id || !payload.workflow_trigger_event) {
    return {
      data: null,
      error: {
        message: 'form_id and workflow_trigger_event are required.',
        code: 'invalid_input'
      }
    }
  }

  return handleQuery(
    supabase.from(TRIGGERS_TABLE).insert(payload).select('*').single(),
    { fallbackMessage: 'Unable to create the workflow trigger.', allowEmpty: false }
  )
}

/**
 * @param {string} triggerId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const deleteWorkflowTrigger = async (triggerId, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase.from(TRIGGERS_TABLE).delete().eq('id', triggerId).select('id').single(),
    { fallbackMessage: 'Unable to delete the workflow trigger.', allowEmpty: false }
  )
}

/** Alias for per-form marketing_form_workflow_triggers (same as listWorkflowTriggers). */
export const listFormWorkflowTriggers = listWorkflowTriggers

// --- Admin: brand templates ---

/**
 * List all brand templates (admin only).
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listBrandTemplates = async (options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase
      .from(BRAND_TEMPLATES_TABLE)
      .select('*')
      .order('is_default', { ascending: false })
      .order('name', { ascending: true }),
    { fallbackMessage: 'Unable to load brand templates.' }
  )
}

/**
 * @param {string} id
 * @param {{ role?: string, userId?: string }} [options]
 */
export const getBrandTemplateById = async (id, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase.from(BRAND_TEMPLATES_TABLE).select('*').eq('id', id).maybeSingle(),
    { fallbackMessage: 'Unable to load the brand template.', allowEmpty: false }
  )
}

/**
 * @param {Record<string, any>} data
 * @param {{ role?: string, userId?: string }} [options]
 */
export const createBrandTemplate = async (data, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const payload = normalizeBrandTemplatePayload(data)
  if (!payload.name) {
    return {
      data: null,
      error: { message: 'Name is required.', code: 'invalid_input' }
    }
  }

  return handleQuery(
    supabase.from(BRAND_TEMPLATES_TABLE).insert(payload).select('*').single(),
    { fallbackMessage: 'Unable to create the brand template.', allowEmpty: false }
  )
}

/**
 * @param {string} id
 * @param {Record<string, any>} data
 * @param {{ role?: string, userId?: string }} [options]
 */
export const updateBrandTemplate = async (id, data, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  const payload = {}
  const fieldMap = {
    name: data.name,
    logo_url: data.logo_url ?? data.logoUrl,
    primary_color: data.primary_color ?? data.primaryColor,
    secondary_color: data.secondary_color ?? data.secondaryColor,
    background_color: data.background_color ?? data.backgroundColor,
    text_color: data.text_color ?? data.textColor,
    font_family: data.font_family ?? data.fontFamily,
    custom_css: data.custom_css ?? data.customCss,
    is_default: data.is_default ?? data.isDefault
  }
  Object.entries(fieldMap).forEach(([key, value]) => {
    if (value !== undefined) payload[key] = value
  })

  return handleQuery(
    supabase.from(BRAND_TEMPLATES_TABLE).update(payload).eq('id', id).select('*').single(),
    { fallbackMessage: 'Unable to update the brand template.', allowEmpty: false }
  )
}

/**
 * @param {string} id
 * @param {{ role?: string, userId?: string }} [options]
 */
export const deleteBrandTemplate = async (id, options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase.from(BRAND_TEMPLATES_TABLE).delete().eq('id', id).select('id').single(),
    { fallbackMessage: 'Unable to delete the brand template.', allowEmpty: false }
  )
}

/**
 * Default brand template for public form pages (anon client).
 */
export const getPublicDefaultBrandTemplate = async () => {
  return handleQuery(
    supabaseAnon
      .from(BRAND_TEMPLATES_TABLE)
      .select('*')
      .eq('is_default', true)
      .limit(1)
      .maybeSingle(),
    { fallbackMessage: 'Unable to load default branding.', allowEmpty: true }
  )
}

/**
 * Default brand template (admin only).
 * @param {{ role?: string, userId?: string }} [options]
 */
export const getDefaultBrandTemplate = async (options = {}) => {
  const ctx = await requireAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase
      .from(BRAND_TEMPLATES_TABLE)
      .select('*')
      .eq('is_default', true)
      .limit(1)
      .maybeSingle(),
    { fallbackMessage: 'Unable to load the default brand template.', allowEmpty: true }
  )
}

// --- Agent ---

/**
 * List responses scoped to the current agent's leads (RLS enforced).
 * @param {string} agentId - Reserved for API consistency; access is scoped by auth + RLS.
 * @param {{ formId?: string, role?: string, userId?: string }} [filters]
 */
export const listResponsesForAgent = async (agentId, { formId, role, userId } = {}) => {
  const ctx = await requireAgentOrAdmin({ role, userId })
  if (ctx.error) return { data: null, error: ctx.error }

  if (ctx.role === 'agent' && agentId && ctx.userId && agentId !== ctx.userId) {
    return accessDeniedResponse('You can only load responses for your own agent account.')
  }

  let query = supabase
    .from(RESPONSES_TABLE)
    .select(RESPONSE_WITH_FORM_SELECT)
    .order('submitted_at', { ascending: false })

  if (formId) query = query.eq('form_id', formId)

  return handleQuery(query, { fallbackMessage: 'Unable to load form responses.' })
}

/**
 * All responses for a lead (agent/admin; RLS enforced).
 * @param {string} leadId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listResponsesForLead = async (leadId, options = {}) => {
  const ctx = await requireAgentOrAdmin(options)
  if (ctx.error) return { data: null, error: ctx.error }

  return handleQuery(
    supabase
      .from(RESPONSES_TABLE)
      .select(RESPONSE_WITH_FORM_SELECT)
      .eq('lead_id', leadId)
      .order('submitted_at', { ascending: false }),
    { fallbackMessage: 'Unable to load responses for this lead.' }
  )
}

// --- Public ---

/**
 * Submit answers for an active form via SECURITY DEFINER RPC (anon client).
 * @param {string} formId
 * @param {string | null} respondentEmail
 * @param {string} respondentName
 * @param {string} respondentPhone
 * @param {Array<{ question_id: string, answer_text?: string | null, selected_option_id?: string | null }>} answers
 */
export const submitFormResponse = async (
  formId,
  respondentEmail,
  respondentName,
  respondentPhone,
  answers
) => {
  if (!formId) {
    return { data: null, error: { message: 'Form id is required.', code: 'invalid_input' } }
  }

  const normalizedAnswers = (Array.isArray(answers) ? answers : []).map((answer) => ({
    question_id: answer.question_id ?? answer.questionId,
    answer_text: answer.answer_text ?? answer.answerText ?? null,
    selected_option_id: answer.selected_option_id ?? answer.selectedOptionId ?? null
  }))

  const { data, error } = await supabaseAnon.rpc('submit_form_response', {
    p_form_id: formId,
    p_respondent_email: respondentEmail ?? null,
    p_respondent_name: respondentName ?? null,
    p_respondent_phone: respondentPhone ?? null,
    p_answers: normalizedAnswers
  })

  if (error) {
    return { data: null, error: formatSupabaseError(error, 'Unable to submit the form.') }
  }

  const result =
    data && typeof data === 'object' && !Array.isArray(data)
      ? data
      : { response_id: data, score: null, passed: null, total_questions: null }

  return {
    data: {
      response_id: result.response_id ?? result.responseId ?? null,
      score: result.score ?? null,
      passed: result.passed ?? null,
      total_questions: result.total_questions ?? result.totalQuestions ?? null
    },
    error: null
  }
}

/**
 * Load an active public form by slug with nested questions and options (anon client).
 * @param {string} slug
 */
export const getPublicForm = async (slug) => {
  if (!slug || String(slug).trim() === '') {
    return { data: null, error: { message: 'Form slug is required.', code: 'invalid_input' } }
  }

  const normSlug = String(slug).trim().toLowerCase()

  const result = await handleQuery(
    supabaseAnon
      .from(FORMS_TABLE)
      .select(FORM_WITH_BRANDING_SELECT)
      .eq('slug', normSlug)
      .eq('status', 'active')
      .maybeSingle(),
    { fallbackMessage: 'Unable to load the form.', allowEmpty: false }
  )

  if (result.error) return result
  return { data: normalizeNestedForm(result.data), error: null }
}
